import { useCallback, useEffect, useRef, useState } from 'react'
import type { SetupAudience } from '@shared/setup'
import { getMyAccountPermissions } from '../../../services/member-access-service'

/**
 * Members/Independents whose account `calls` permission is off: call steps
 * become optional and call recording isn't asked. Read once per launch and
 * again when the app comes back after 15+ minutes (`refresh`).
 */
const callsEnabledCache = new Map<string, boolean>()

/** `null` when the read failed (keep what we knew). */
function readCallsEnabled(): Promise<boolean | null> {
  return getMyAccountPermissions()
    .then((p) => p?.calls !== 'disabled')
    .catch(() => null)
}

export function useCallsEnabled(
  userId: string | null,
  audience: SetupAudience | null,
): [boolean | null, () => Promise<void>] {
  const gated = audience === 'member' || audience === 'independent'
  const [value, setValue] = useState<boolean | null>(() =>
    !gated ? true : userId ? (callsEnabledCache.get(userId) ?? null) : null,
  )
  const currentUser = useRef(userId)
  currentUser.current = userId
  useEffect(() => {
    if (!userId || !gated) {
      setValue(true)
      return
    }
    const cached = callsEnabledCache.get(userId)
    if (cached != null) {
      setValue(cached)
      return
    }
    let cancelled = false
    void readCallsEnabled().then((enabled) => {
      // A failed first read assumes calls are on (the default) without caching it.
      if (enabled != null) callsEnabledCache.set(userId, enabled)
      if (!cancelled) setValue(enabled ?? true)
    })
    return () => {
      cancelled = true
    }
  }, [userId, gated])
  const refresh = useCallback(async () => {
    if (!userId || !gated) return
    const enabled = await readCallsEnabled()
    // A failed re-read keeps what we knew; a late answer for another account is dropped.
    if (enabled == null || currentUser.current !== userId) return
    callsEnabledCache.set(userId, enabled)
    setValue(enabled)
  }, [userId, gated])
  return [value, refresh]
}
